<template>
  <DialogPrime
    :visible="visible"
    modal
    :style="{ width: width }"
    :class="dialogClass"
    @update:visible="onDialogClose"
  >
    <template #header>
      <div class="d-flex align-items-center justify-content-between w-100 pe-3">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <h5 class="mb-0 fw-bold text-dark">{{ t(config.title) }}</h5>
          <small
            v-if="errorMessage"
            class="text-danger d-flex align-items-center"
            style="font-style: italic; font-size: 0.75rem;"
          >
            <i class="fas fa-exclamation-circle me-1"></i> {{ errorMessage }}
          </small>
        </div>
      </div>
    </template>

    <div class="row g-4">
      <!-- File Input -->
      <div class="col-12">
        <label for="import-file" class="form-label fw-semibold">
          {{ t('Select File') }} ({{ config.acceptTypes.toUpperCase().replace(/\./g, ' ') }})
          <span class="text-danger">*</span>
        </label>
        <input
          id="import-file"
          type="file"
          :accept="config.acceptTypes"
          class="form-control"
          :disabled="importing"
          @change="onFileChange"
        />
        <small v-if="fileName" class="text-muted mt-1 d-block">
          <i class="pi pi-file me-1"></i> {{ fileName }}
        </small>
        <!-- Download Template: link kecil di bawah input file (bukan tombol selebar modal). -->
        <a v-if="config.showTemplateButton" href="#" class="import-template-link mt-2"
          :class="{ 'is-loading': templateLoading }" :aria-disabled="templateLoading"
          @click.prevent="!templateLoading && downloadTemplate()">
          <i :class="templateLoading ? 'pi pi-spin pi-spinner' : 'pi pi-download'"></i>
          {{ t(config.templateLabel) }}
        </a>
      </div>

      <!-- Validation Errors Display -->
      <div v-if="validationErrors.length > 0" class="col-12">
        <div class="import-error-box">
          <div class="import-error-top">
            <i class="fas fa-exclamation-circle"></i>
            <span>Import failed — {{ validationErrors.length }} invalid {{ validationErrors.length === 1 ? 'row' : 'rows' }}. Fix the issues below and try again.</span>
          </div>
          <DataTable
            :value="validationErrors"
            class="p-datatable-sm p-datatable-gridlines import-error-table"
            scrollable
            scroll-height="180px"
            table-style="min-width: 100%; width: 100%"
          >
            <Column field="row" header="Row" header-class="bg-light" :style="{ width: '50px', textAlign: 'center' }" body-class="text-center" />
            <Column field="field" header="Column" header-class="bg-light" :style="{ width: '130px' }" />
            <Column field="value" header="Value" header-class="bg-light" :style="{ width: '140px' }">
              <template #body="{ data }">
                <span>{{ data.value || '-' }}</span>
              </template>
            </Column>
            <Column field="message" header="Error Messange" header-class="bg-light" />
          </DataTable>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage && showSuccessInline" class="col-12">
        <div class="alert alert-success mb-0">
          <i class="fas fa-check-circle me-2"></i>
          {{ successMessage }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
        <ButtonPrime
          type="button"
          :label="t(config.cancelLabel)"
          severity="danger"
          raised
          size="small"
          class="px-4 border-0"
          :disabled="importing"
          @click="onCancel"
        />
        <ButtonPrime
          type="button"
          severity="contrast"
          raised
          size="small"
          :disabled="!canImport"
          :label="t(config.importLabel)"
          class="px-4"
          @click="onImport"
        >
          <template v-if="importing" #icon>
            <i class="fas fa-spinner fa-spin me-2"></i>
          </template>
        </ButtonPrime>
      </div>
    </template>
  </DialogPrime>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import translate from '@/helpers/translate';

export default {
  name: 'ImportExcelModal',
  props: {
    // Module namespace in Vuex (e.g., 'importExcel')
    module: {
      type: String,
      default: 'importExcel',
    },
    // Dialog width
    width: {
      type: String,
      default: '65rem',
    },
    // Dialog CSS class
    dialogClass: {
      type: String,
      default: 'import-excel-modal',
    },
    // Custom config overrides
    configOverrides: {
      type: Object,
      default: () => ({}),
    },
    // Service function for import (receives id and file)
    importService: {
      type: Function,
      default: null,
    },
    // Service function for template download
    templateService: {
      type: Function,
      default: null,
    },
    // ID for the import endpoint (e.g., route param id)
    importId: {
      type: [String, Number],
      default: null,
    },
    // Callback after successful import
    onSuccess: {
      type: Function,
      default: null,
    },
    // Callback after dialog close
    onClose: {
      type: Function,
      default: null,
    },
    // Show inline success alert inside modal
    showSuccessInline: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const modulePath = props.module;

    // Computed state from Vuex
    const visible = computed(() => store.state[modulePath]?.visible ?? false);
    const templateLoading = computed(() => store.state[modulePath]?.templateLoading ?? false);
    const importing = computed(() => store.state[modulePath]?.importing ?? false);
    const fileName = computed(() => store.state[modulePath]?.fileName ?? '');
    const errors = computed(() => store.state[modulePath]?.errors ?? []);
    const errorMessage = computed(() => store.state[modulePath]?.errorMessage ?? '');
    const successMessage = computed(() => store.state[modulePath]?.successMessage ?? '');
    const config = computed(() => store.state[modulePath]?.config ?? {});

    // Getters
    const canImport = computed(() => store.getters[`${modulePath}/canImport`]);
    const validationErrors = computed(() => store.getters[`${modulePath}/allErrors`]);

    // Methods
    const t = (key) => {
      try {
        const translated = translate.tt(key);
        if (translated && !translated.startsWith('Unknown')) return translated;
      } catch (e) { /* dictionary bahasa belum dimuat */ }
      return key;
    };

    const onDialogClose = (value) => {
      if (!value) {
        store.dispatch(`${modulePath}/closeDialog`);
        emit('close');
        props.onClose?.();
      }
    };

    const onCancel = () => {
      store.dispatch(`${modulePath}/closeDialog`);
      emit('close');
      props.onClose?.();
    };

    const onFileChange = (event) => {
      const file = event.target.files?.[0] || null;
      store.dispatch(`${modulePath}/setFile`, file);
    };

    const downloadTemplate = async () => {
      // Service template bisa dari prop (mis. generalMasterData) ATAU dari store (`importExcel/openDialog`, dipakai
      // New_McrItem) -- dulu cuma prop yang dicek, jadi Download Template dari New_McrItem tidak melakukan apa-apa.
      const service = props.templateService || store.state[modulePath]?.templateService;
      if (service) {
        await store.dispatch(`${modulePath}/downloadTemplate`);
      }
    };

    const onImport = async () => {
      const serviceFn = props.importService;
      const result = await store.dispatch(`${modulePath}/importFile`, {
        id: props.importId,
        serviceFn,
      });

      if (result) {
        emit('success');
        props.onSuccess?.();
      }
    };

    return {
      visible,
      templateLoading,
      importing,
      fileName,
      errors,
      errorMessage,
      successMessage,
      config,
      canImport,
      validationErrors,
      t,
      onDialogClose,
      onCancel,
      onFileChange,
      downloadTemplate,
      onImport,
    };
  },
};
</script>

<style scoped>
.import-template-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  /* Ukuran sama dengan label "Select File" (.form-label tema Argon), warna primary tema (biru). */
  font-size: 0.75rem;
  color: #185ea2;
  text-decoration: none;
}
.import-template-link:hover,
.import-template-link:focus {
  text-decoration: none;
}
.import-template-link.is-loading {
  pointer-events: none;
  opacity: 0.6;
}
.import-excel-modal {
  max-height: 90vh;
  overflow-y: auto;
}

.import-error-box {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.import-error-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  font-size: 13px;
  color: #991b1b;
  font-weight: 500;
}

.import-error-top i {
  color: #dc2626;
  font-size: 14px;
}

.import-error-table :deep(.p-datatable-thead > tr > th) {
  font-size: 0.75rem;
  padding: 4px 8px;
}

.import-error-table :deep(.p-datatable-tbody > tr > td) {
  font-size: 0.8rem;
  padding: 6px 8px;
}

:deep(.alert-success) {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
  border-radius: 8px;
  padding: 12px 16px;
}
</style>
